<template>
    <div class="cap-dash">
        <div class="cap-subtabs">
            <button v-for="t in subTabs" :key="t.key" class="cap-subtab" :class="{active: activeSub===t.key}" @click="activeSub=t.key">{{t.label}}</button>
        </div>

        <!-- ========== 日产能 ========== -->
        <div v-show="activeSub==='daily'" class="cap-panel">
            <div class="dayrate-banner">
                🗓️ 这里填的所有数量都是 <b>日产能</b>——<b>每个工作日的标准产能，每天重复</b>，不是一次性总量。周末/节假日不排产，临时加班/外包到「扩充产能」按日期补。
            </div>
            <div class="confirm-note">
                ⚠️ <b>编辑权限</b>按角色配置分项授予：<b>添加部门 / 移除部门 / 保存产能 / 标签维护</b>各自独立，没勾对应权限则相应按钮不显示、只读。一级、二级标签均可自定义增删；加了二级的一级数量为只读汇总，上层自动 SUM 重算。<br>
                ➕ <b>添加标签同样纳入「保存」</b>：点「＋添加一级/二级标签」只在页面上加一行（标「未保存」），点「保存日产能」才真正创建；<b>直接关页面 = 放弃本次新增</b>。可以先加一级、再在它下面加二级，一起保存。<br>
                🗑️ <b>删除标签同样纳入「保存」</b>：点 × 只是<b>本地标记</b>（划掉变灰、可撤销），点「保存日产能」才真正删除；<b>直接关页面 = 放弃本次删除</b>。（未保存的新增行点 × 直接移除，无需再保存）
            </div>
            <div class="add-dept-bar" v-if="canAddDept || canSaveDaily">
                <el-button v-if="canAddDept" type="primary" size="small" icon="el-icon-plus" @click="openAddDialog">添加部门（制作组）</el-button>
                <el-button v-if="canSaveDaily" type="success" size="small" icon="el-icon-check" :disabled="!dirtyDaily" @click="saveDaily">保存日产能<span v-if="dirtyDaily">（有未保存修改{{pendingDailyText}}）</span></el-button>
                <span class="lbl-tip">改完日产能数字、加好新标签、标好要删的标签，统一点「保存」一次提交；从组织架构选制作组纳入，系统自动按其上级部门归入大类</span>
            </div>

            <div v-loading="loading">
                <div v-for="cat in cats" :key="cat.id" class="cap-tree-cat">
                    <div class="tree-cat-head">
                        <span class="tc-name">{{cat.name}}（上级部门 / 大类）
                            <el-tag v-if="cat.cat_type==1" size="mini" type="primary" class="ml6">视频</el-tag>
                            <el-tag v-else-if="cat.cat_type==2" size="mini" type="success" class="ml6">平面</el-tag>
                            <span v-else class="cat-undecided">
                                ⚠️ 未识别大类，请选择：
                                <template v-if="canAddDept">
                                    <el-button size="mini" type="primary" plain @click="setCatType(cat,1)">视频</el-button>
                                    <el-button size="mini" type="success" plain @click="setCatType(cat,2)">平面</el-button>
                                </template>
                                <span v-else>（需小组长设置）</span>
                            </span>
                        </span>
                        <span class="tc-total">大类总产能 <b>{{catTotal(cat)}}</b>（=Σ其下各制作组日产能）</span>
                    </div>
                    <div v-for="g in cat.groups" :key="g.id" class="tree-group">
                        <div class="tree-group-head">
                            <span class="tg-name">{{g.name}} <span class="grp-pill">上级：{{g.parent_name}}</span></span>
                            <span class="tg-right">
                                <span class="tg-sum">组日产能（SUM） <b>{{groupSum(g)}}</b> · 80%上限 <b>{{(groupSum(g)*0.8).toFixed(1)}}</b></span>
                                <span v-if="g.last_edit_name || g.last_mtime" class="tg-edit">最近修改：{{g.last_edit_name||'-'}} · {{g.last_mtime||'-'}}</span>
                                <el-button v-if="canRemoveDept" type="text" class="rm-grp" @click="removeGroup(g)">移除部门</el-button>
                            </span>
                        </div>
                        <div v-for="t1 in g.tags1" :key="t1._k" class="tree-t1" :class="{'row-del': t1._del, 'row-new': t1._new}">
                            <div class="tree-t1-head">
                                <span class="t1-name">{{t1.name}}</span>
                                <el-tag v-if="t1.is_default==1" size="mini" class="ml4">默认</el-tag>
                                <span v-if="t1._new" class="new-flag">未保存·新增，点「保存日产能」后创建</span>
                                <span v-if="t1._del" class="del-flag">已标记删除（连同二级），点「保存日产能」后生效</span>
                                <span v-if="liveSubs(t1).length" class="t1-num readonly">一级日产能合计 <b>{{sumSubs(t1)}}</b><span class="lbl-tip">（=各二级之和·只读）</span></span>
                                <span v-else class="t1-num">日产能
                                    <input type="number" min="0" v-model.number="t1.num" :disabled="!canSaveDaily || t1._del"
                                           @input="dirtyDaily=true" title="每工作日标准产能"><span class="lbl-tip">/工作日</span>
                                </span>
                                <span v-if="carryHint(t1)" class="carry-hint">保存时会把该一级<b>已保存的</b>日产能 {{carryHint(t1)}} 自动转成二级「原产能」保留（不会丢）</span>
                                <span v-if="canEditLabel" class="t1-actions">
                                    <template v-if="!t1._del">
                                        <button class="sub-add" @click="addSub(t1)">＋ 添加二级标签</button>
                                        <i class="t1-del" :title="t1._new?'移除这条未保存的新增一级标签（连同其下未保存的二级）':'标记删除该一级标签（连同二级）·点保存后生效'" @click="delLabel(t1, true, g)">×</i>
                                    </template>
                                    <button v-else class="undo-btn" @click="undoDelLabel(t1)">↺ 撤销删除</button>
                                </span>
                            </div>
                            <div v-if="t1.subs && t1.subs.length" class="tree-subs">
                                <span v-for="s in t1.subs" :key="s._k" class="sub" :class="{'row-del': s._del, 'row-new': s._new}">{{s.name}}<i v-if="s._new" class="new-dot" title="未保存的新增二级标签，点「保存日产能」后创建">新</i>
                                    <input type="number" min="0" v-model.number="s.num" :disabled="!canSaveDaily || s._del || t1._del" @input="dirtyDaily=true">
                                    <i v-if="canEditLabel && !s._del && !t1._del" class="sub-del" :title="s._new?'移除这条未保存的新增二级标签':'标记删除二级标签·点保存后生效'" @click="delLabel(s, false, g, t1)">×</i>
                                    <button v-if="canEditLabel && s._del && !t1._del" class="undo-btn mini" @click="undoDelLabel(s)">↺ 撤销</button>
                                </span>
                            </div>
                            <div v-else class="tree-subs-empty">{{canEditLabel?'（无二级标签，直接在上方填数；点「＋添加二级标签」可拆分）':'（无二级标签，直接计数）'}}</div>
                        </div>
                        <div v-if="!g.tags1 || !g.tags1.length" class="t1-empty">（该组暂无一级标签）</div>
                        <div v-if="canEditLabel" class="t1-add-row"><button class="t1-add" @click="addT1(g)">＋ 添加一级标签</button></div>
                    </div>
                </div>
                <div v-if="!cats.length" class="t1-empty">（暂无已纳入的制作组，点「添加部门」从组织架构选入）</div>
            </div>
        </div>

        <!-- ========== 扩充产能（按天·制作组(叶组)级） ========== -->
        <div v-show="activeSub==='extra'" class="cap-panel">
            <div class="confirm-note">
                ⚠️ <b>扩充产能（按天）</b>：给「某日期 + 某制作组」临时加产能（加班/外包）。该制作组当日产能 = 日产能 + 当天扩充。<b>仅创意负责人可编辑</b>，只加不减。
            </div>
            <div v-if="canEditExpand" class="extra-form">
                <el-date-picker v-model="extraForm.date" type="date" size="small" value-format="yyyy-MM-dd" placeholder="选择日期" :picker-options="expandPickerOptions"/>
                <el-select v-model="extraForm.group_id" size="small" placeholder="制作组" style="width:180px">
                    <el-option v-for="g in configGroups" :key="g.id" :value="g.id" :label="g.name"/>
                </el-select>
                <el-input-number v-model="extraForm.num" :min="1" :max="99999" size="small" controls-position="right" style="width:120px"/>
                <el-button type="primary" size="small" @click="addExtra">＋ 添加扩充</el-button>
                <span class="lbl-tip">例：6/22 视频组 +20 应对加班</span>
            </div>
            <el-table :data="extraList" stripe :header-cell-style="{background:'#e8eaec',color:'#333'}" style="width:100%">
                <el-table-column prop="expand_date" label="日期" align="center" width="130"/>
                <el-table-column label="部门" align="center" width="180">
                    <template slot-scope="{row}"><span v-if="row.parent_name" style="color:#909399">{{row.parent_name}} / </span>{{row.group_name}}</template>
                </el-table-column>
                <el-table-column label="扩充数量" align="center" width="100">
                    <template slot-scope="{row}"><b style="color:#67C23A">+{{row.expand_num}}</b></template>
                </el-table-column>
                <el-table-column prop="cap_note" label="说明（当日产能合计）" align="left" min-width="260" show-overflow-tooltip/>
                <el-table-column prop="edit_name" label="操作人" align="center" width="100"/>
                <el-table-column prop="mtime" label="最新修改时间" align="center" width="140"/>
                <el-table-column v-if="canEditExpand" label="操作" align="center" width="80">
                    <template slot-scope="{row}"><el-button type="text" @click="delExtra(row)"><span style="color:#F56C6C">删除</span></el-button></template>
                </el-table-column>
            </el-table>
        </div>

        <!-- ========== 渠道占比（大类组级·纯看板） ========== -->
        <div v-show="activeSub==='channel'" class="cap-panel">
            <p class="chan-tip">
                ⚠️ 渠道从投放平台字典选择添加（避免错名/重复）。渠道占比主要用于看板对比；<b>「渠道级占比」在开启「排期引擎媒体配额」后会参与一键/定时预排的媒体保底分配</b>（档级细分暂不参与）。{{canEditRatio?'':'（当前角色只读）'}}<br>
                结构为<b>两层</b>：<b>渠道级占比</b>（大类总产能在各渠道之间怎么分）＋ <b>档级占比</b>（该渠道内部再按 产品类型 / 包体类型 / 游戏类型 细分）。
                两层合计<b>都必须为 100%</b>，否则无法保存；只加渠道<b>不打标也合法</b>（该渠道不做档级细分）。某档绝对占比 = 渠道级 × 档级 ÷ 100。<br>
                🗑️ <b>删除同样纳入「保存占比」</b>：点 × 只是<b>本地标记</b>（划掉变灰、可撤销），点「保存占比」才真正删除；<b>直接关页面 = 放弃本次删除</b>。删掉渠道后记得把<b>剩余占比重新配满 100%</b>再保存。
            </p>
            <div v-for="cat in cats" :key="cat.id" class="chan-cat">
                <div class="chan-cat-head">
                    <span class="cc-name">{{cat.name}}（大类总产能 {{catTotal(cat)}}）</span>
                    <span class="cc-total">
                        <span v-if="lastChan(cat.id)" class="tg-edit mr10">最近修改：{{lastChan(cat.id).edit_name||'-'}} · {{lastChan(cat.id).mtime||'-'}}</span>
                        按总产能分配各渠道 · {{canEditRatio?'可编辑':'只读'}}
                    </span>
                </div>
                <div class="chan-rows">
                    <div v-for="node in (channelMap[cat.id]||[])" :key="node._k" class="chan-tree" :class="{'row-del': node._del}">
                        <div class="chan-row">
                            <i class="cr-fold" :class="node.expanded?'el-icon-caret-bottom':'el-icon-caret-right'"
                               :title="node.expanded?'收起档级明细':'展开档级明细'" @click="node.expanded=!node.expanded"></i>
                            <span class="cr-name">{{node.plat_name}}</span>
                            <span class="cr-bar"><i :style="{width: Math.min(100, node.ratio)+'%'}"></i></span>
                            <span class="cr-pct">
                                <input v-if="canEditRatio" type="number" min="0" max="100" :value="node.ratio" :disabled="node._del" @change="e=>node.ratio=+e.target.value||0">
                                <b v-else>{{node.ratio}}</b>%
                            </span>
                            <span class="cr-num">≈ {{(catTotal(cat)*node.ratio/100).toFixed(1)}} 产能</span>
                            <span v-if="node._del" class="cr-tsum del">已标记删除（连同其档级明细），点「保存占比」后生效</span>
                            <span v-else-if="liveTiers(node).length" class="cr-tsum" :class="tierSum(node)===100?'ok':'bad'">
                                档级合计 {{tierSum(node)}}%{{tierSum(node)===100?'（✔ 已配满）':'（⚠️ 需为 100%，否则无法保存）'}}
                            </span>
                            <span v-else class="cr-tsum none">未打标（该渠道不做档级细分）</span>
                            <button v-if="canEditRatio && !node._del" class="cr-del" title="标记删除该渠道（连同其下全部档级明细）·点保存后生效" @click="delChanNode(cat.id, node)">×</button>
                            <button v-else-if="canEditRatio" class="undo-btn" @click="undoDelChan(node)">↺ 撤销删除</button>
                        </div>
                        <div v-show="node.expanded" class="chan-tiers">
                            <div v-for="t in node.tiers" :key="t._k" class="chan-tier" :class="{'row-del': t._del || node._del}">
                                <template v-if="canEditRatio && !t._del && !node._del">
                                    <el-select v-model="t.app_type" size="mini" placeholder="产品类型" style="width:96px" @change="onTierAppType(t)">
                                        <el-option v-for="o in appTypeOptions" :key="o.v" :value="o.v" :label="o.l"/>
                                    </el-select>
                                    <el-select v-model="t.package_type" size="mini" placeholder="包体类型" style="width:124px">
                                        <el-option v-for="o in packageTypeOptions" :key="o.v" :value="o.v" :label="o.l"/>
                                    </el-select>
                                    <el-select v-if="t.app_type==2" v-model="t.game_classify" size="mini" placeholder="游戏类型" style="width:96px">
                                        <el-option v-for="o in gameClassifyOptions" :key="o.v" :value="o.v" :label="o.l"/>
                                    </el-select>
                                    <span v-else class="tier-na">{{t.app_type==1?'（应用不适用游戏类型）':'（选「游戏」才需填游戏类型）'}}</span>
                                </template>
                                <span v-else class="tier-text">{{tierText(t)}}</span>
                                <span class="cr-bar"><i :style="{width: Math.min(100, t.ratio)+'%'}"></i></span>
                                <span class="cr-pct">
                                    <input v-if="canEditRatio" type="number" min="0" max="100" :value="t.ratio" :disabled="t._del || node._del" @change="e=>t.ratio=+e.target.value||0">
                                    <b v-else>{{t.ratio}}</b>%
                                </span>
                                <span class="cr-num" title="绝对占比 = 渠道级占比 × 档级占比 ÷ 100">绝对 {{absRatio(node,t)}}%</span>
                                <button v-if="canEditRatio && !t._del && !node._del" class="cr-del" title="标记删除该组合·点保存后生效" @click="delTier(cat.id, node, t)">×</button>
                                <button v-else-if="canEditRatio && t._del && !node._del" class="undo-btn" @click="undoDelTier(t)">↺ 撤销删除</button>
                            </div>
                            <div v-if="!liveTiers(node).length" class="tier-empty">（该渠道未打标：只按渠道级占比统计，不校验档级合计）</div>
                            <div v-if="canEditRatio && !node._del" class="tier-add-row"><button class="tier-add" @click="addTier(node)">＋ 添加组合</button></div>
                        </div>
                    </div>
                    <div v-if="!(channelMap[cat.id]||[]).length" class="chan-empty">暂无渠道，点「＋添加渠道」新增</div>
                </div>
                <template v-if="canEditRatio">
                    <div class="chan-add-bar">
                        <el-select v-model="newPlat[cat.id]" size="mini" clearable filterable placeholder="选择渠道" style="width:160px">
                            <el-option v-for="p in platOptions" :key="p.id" :value="p.id" :label="p.name"/>
                        </el-select>
                        <button class="cr-add" @click="addChanRow(cat.id)">＋ 添加渠道</button>
                        <el-button type="primary" size="mini" class="ml10" @click="saveChannel(cat.id)">保存占比</el-button>
                        <span v-if="delChanCount(cat.id)" class="del-flag">待删除 {{delChanCount(cat.id)}} 项，点「保存占比」后生效</span>
                    </div>
                </template>
                <div class="chan-sum" :class="sumRatio(cat.id)===100?'ok':'bad'">渠道级占比合计：{{sumRatio(cat.id)}}%{{sumRatio(cat.id)===100?'（✔ 已配满）':'（⚠️ 合计需为 100%，否则无法保存）'}}</div>
            </div>
            <div v-if="!cats.length" class="t1-empty">（暂无已纳入的制作组，先到「日产能」添加部门）</div>
        </div>

        <!-- ========== 历史修改记录 ========== -->
        <div v-show="activeSub==='history'" class="cap-panel">
            <div class="hist-bar">
                <span class="mr6">类型：</span>
                <el-select v-model="historyType" size="small" clearable placeholder="全部" style="width:140px">
                    <el-option :value="54" label="产能标签"/>
                    <el-option :value="55" label="扩充产能"/>
                    <el-option :value="56" label="渠道占比"/>
                </el-select>
                <el-button type="primary" size="small" class="ml10" icon="el-icon-search"
                           :loading="historyLoading" :disabled="historyLoading" @click="reloadHistory">查询</el-button>
            </div>
            <el-table v-loading="historyLoading" :data="historyList" stripe :header-cell-style="{background:'#e8eaec',color:'#333'}" style="width:100%">
                <el-table-column prop="ctime" label="时间" align="center" width="150"/>
                <el-table-column prop="operator" label="操作人" align="center" width="120"/>
                <el-table-column prop="type_text" label="类型" align="center" width="110"/>
                <el-table-column prop="detail" label="详情" align="left" min-width="240" show-overflow-tooltip/>
            </el-table>
            <div class="tar mt10">
                <el-pagination background layout="total, prev, pager, next" :total="historyTotal"
                               :current-page="historyPage" :page-size="historyPageSize" @current-change="onHistoryPage"/>
            </div>
        </div>

        <!-- 添加部门弹框：从组织架构选制作组（多选，权限范围内） -->
        <el-dialog title="添加部门（制作组）" :visible.sync="addDialog" width="460px" append-to-body>
            <p class="lbl-tip" style="margin-bottom:8px">从组织架构选择制作组（可多选）。纳入后系统按其上级部门自动归入大类，并预置默认标签 应用/IAA/IAP。</p>
            <el-cascader v-model="addSelected" :options="groupTree" :props="addProps" filterable clearable
                         placeholder="选择制作组" style="width:100%"/>
            <span slot="footer">
                <el-button @click="addDialog=false">取消</el-button>
                <el-button type="primary" :disabled="!addSelected.length" @click="confirmAdd">确定纳入</el-button>
            </span>
        </el-dialog>

        <!-- 删组迁移弹框：该组有在册排期时，迁移到目标组再删（仅创意负责人·成本高） -->
        <el-dialog title="删除组并迁移排期" :visible.sync="migrate.visible" width="500px" append-to-body>
            <div class="migrate-warn">
                ⚠️ 「<b>{{migrate.srcGroup && migrate.srcGroup.name}}</b>」有 <b class="hot">{{migrate.pending}}</b> 单已排/已定版排期。
                删除该组将：<b>解除已定版 → 作废进行中审批 → 把这些单整体迁移到目标组</b>（已排单·保留交付日·可跨大类）。
                <div class="migrate-cost">删除组成本很高，且<b>引擎不会自动重排这些单</b>；迁移后请<b>及时更新目标组产能</b>。</div>
            </div>
            <div class="migrate-pick">
                <span class="mp-lbl">迁移目标组</span>
                <el-select v-model="migrate.targetGroup" size="small" placeholder="选择目标制作组（现有产能配置组）" style="width:300px" filterable>
                    <el-option v-for="g in migrateTargetOptions()" :key="g.id" :value="g.id" :label="g.parent_name ? (g.parent_name + ' / ' + g.name) : g.name"/>
                </el-select>
            </div>
            <span slot="footer">
                <el-button @click="migrate.visible=false">取消</el-button>
                <el-button type="danger" :loading="migrate.loading" :disabled="!migrate.targetGroup" @click="confirmMigrate">确认删除并迁移</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
    import API from '@/api/workOrder/conf.js';

    export default {
        name: 'CapacityConfigDashboard',
        props: { pageFunc: Array },
        data() {
            return {
                activeSub: 'daily',
                subTabs: [{ key: 'daily', label: '日产能' }, { key: 'extra', label: '扩充产能' }, { key: 'channel', label: '渠道占比' }, { key: 'history', label: '历史修改记录' }],
                loading: false,
                dirtyDaily: false,         // 日产能有未保存修改（本地编辑，点保存才提交）
                labelSeq: 0,               // 新增标签行的本地临时 key 自增（未落库行没有 id，且要给同批新增的二级当父指针）
                configGroups: [],          // 已纳入制作组（自下而上，含 parent_id/parent_name）
                // 添加部门
                addDialog: false,
                addSelected: [],
                // 删组迁移（该组有在册排期时·迁到目标组再删·仅创意负责人）
                migrate: { visible: false, srcGroup: null, pending: 0, targetGroup: '', loading: false },
                groupTree: [],             // 权限范围真实组织子树（选制作组）
                addProps: { value: 'id', label: 'name', children: 'children', checkStrictly: true, multiple: true, emitPath: false, expandTrigger: 'click' },
                // 扩充
                extraForm: { date: '', group_id: '', num: 10 },
                extraList: [],
                // 扩充日期不能选过去（时光不能倒流·今天可选·与服务端校验对齐）
                expandPickerOptions: { disabledDate(time) { return time.getTime() < Date.now() - 864e5; } },
                // 渠道（key = 上级部门/大类组 id · value = 以渠道为根的树）
                platOptions: [],
                channelMap: {},
                newPlat: {},
                tierSeq: 0,                // 新增档级行的本地 key 自增（未落库行没有 id）
                // 档级维度全集（设计 Q2「能配的都要给」·不预设不裁剪）
                appTypeOptions: [{ v: 1, l: '应用' }, { v: 2, l: '游戏' }],
                packageTypeOptions: [{ v: 11, l: '安卓应用' }, { v: 12, l: '安卓快应用' }, { v: 21, l: 'iOS应用' }, { v: 3, l: '小程序' }, { v: 41, l: '鸿蒙应用' }],
                gameClassifyOptions: [{ v: 3, l: 'IAA' }, { v: 4, l: 'IAP' }],
                // 历史修改记录
                historyList: [],
                historyTotal: 0,
                historyLoading: false,
                historyType: '',
                historyPage: 1,
                historyPageSize: 20,
            };
        },
        computed: {
            pageFuncNames() { return (this.pageFunc || []).map(i => i.name); },
            // 2026-06-30 节点细化：原 edit_capacity_label 拆 添加部门/移除部门/保存产能/标签维护(本节点保留)
            canEditLabel() { return this.pageFuncNames.includes('edit_capacity_label'); },   // 标签维护(一/二级 增删改)
            canAddDept() { return this.pageFuncNames.includes('add_capacity_dept'); },        // 添加部门(含设大类类型)
            canRemoveDept() { return this.pageFuncNames.includes('remove_capacity_dept'); },  // 移除部门
            canSaveDaily() { return this.pageFuncNames.includes('save_capacity_daily'); },    // 保存日产能
            canEditExpand() { return this.pageFuncNames.includes('edit_capacity_expand'); },
            canEditRatio() { return this.pageFuncNames.includes('edit_channel_ratio'); },
            // 本地已标记删除的标签数（含被一级带走的二级·仅提示用）
            delDailyCount() { return this.collectDeletedLabelIds().length; },
            // 本地待新增的标签数（仅提示用）
            addDailyCount() { return this.collectAddedLabels().length; },
            // 保存按钮上的待办摘要（新增/删除各多少项）
            pendingDailyText() {
                const parts = [];
                if (this.addDailyCount) parts.push('待新增 ' + this.addDailyCount + ' 项');
                if (this.delDailyCount) parts.push('待删除 ' + this.delDailyCount + ' 项');
                return parts.length ? '·' + parts.join('·') : '';
            },
            // 按上级部门(parent_id)把制作组归成大类块
            cats() {
                const map = {};
                (this.configGroups || []).forEach(g => {
                    const pid = g.parent_id;
                    if (!map[pid]) map[pid] = { id: pid, name: g.parent_name, cat_type: g.parent_cat_type || 0, groups: [] };
                    map[pid].groups.push(g);
                });
                return Object.values(map);
            },
        },
        mounted() {
            this.loadConfigGroups();
            this.loadExtra();
            this.loadPlatOptions();
        },
        watch: {
            // 每次切 tab 都重取该 tab 的数据（否则跨 tab 编辑保存后回看是陈旧值）
            activeSub(v) {
                if (v === 'history') {
                    this.reloadHistory();
                } else if (v === 'extra') {
                    this.loadExtra();
                } else if (v === 'channel') {
                    // 渠道占比依赖 cats（由 configGroups 派生），只需重取占比行 + 媒体字典
                    this.loadPlatOptions();
                    this.loadAllChannels();
                } else if (v === 'daily') {
                    // 有未保存的本地编辑时不重载，否则用户输了一半的日产能会被服务器值冲掉
                    if (!this.dirtyDaily) this.loadConfigGroups();
                }
            },
        },
        methods: {
            // ---- 已纳入制作组（自下而上）----
            async loadConfigGroups() {
                this.loading = true;
                try {
                    const { data } = await API.getCapacityConfigGroups();
                    // 本地态字段（_del 删除标记 / _new 未落库新增 / _k 渲染 key / _srvNum 服务端原值）
                    // **先于赋值**挂上，Vue 才会把它们纳入响应式（后挂需 $set，徒增心智负担）。
                    // _k：新增行没有 id，多行 id 全是 0 会让 v-for 的 key 撞车（整行错位/输入框串值），
                    //     所以渲染 key 一律走 _k —— 已落库用 'l|s'+id，新增用 addT1/addSub 发的 'n'+seq。
                    // _srvNum：服务端已保存的日产能。B3 结转提示要用「库里的值」而不是用户改了一半的值。
                    (data || []).forEach(g => (g.tags1 || []).forEach(t1 => {
                        t1._del = false;
                        t1._new = false;
                        t1._k = 'l' + t1.id;
                        t1._srvNum = +t1.num || 0;
                        (t1.subs || []).forEach(s => { s._del = false; s._new = false; s._k = 's' + s.id; });
                    }));
                    this.configGroups = data || [];
                    this.dirtyDaily = false;   // 服务器最新数据，清脏标记（含清掉上一轮的删除标记与未保存新增）
                    this.loadAllChannels();
                } catch (error) {
                    this.$message.error(error.info || '加载失败');
                } finally {
                    this.loading = false;
                }
            },
            // 实时本地汇总（编辑时即时反映，不等保存）—— 已标记删除的行一律不计入，
            // 否则页面上的合计/大类总产能会和「保存后的真实结果」对不上（渠道占比 tab 还要拿 catTotal 折算产能）
            liveSubs(t1) { return (t1.subs || []).filter(s => !s._del); },
            sumSubs(t1) { return this.liveSubs(t1).reduce((a, s) => a + (+s.num || 0), 0); },
            groupSum(g) {
                return (g.tags1 || []).filter(t1 => !t1._del)
                    .reduce((a, t1) => a + (this.liveSubs(t1).length ? this.sumSubs(t1) : (+t1.num || 0)), 0);
            },
            catTotal(cat) { return (cat.groups || []).reduce((a, g) => a + this.groupSum(g), 0); },
            // 收集末层日产能（二级 或 无二级的一级）—— 跳过已标记删除的行 与 未落库的新增行
            // ⚠️ items 是**严格按 id 改数**的契约（后端逐条走 update()），新增行没有 id，
            //    它们的名称/层级/父指针/日产能全部由 collectAddedLabels() 走 added_items 提交，别混进来。
            collectDaily() {
                const items = [];
                (this.configGroups || []).forEach(g => (g.tags1 || []).forEach(t1 => {
                    if (t1._del) return;                       // 一级已标删 → 连它的二级一起不提数
                    const subs = this.liveSubs(t1);
                    if (subs.length) {
                        // 一级含存活二级（哪怕全是本次新增的）→ 一级不进 items：后端 update() 有「含二级禁直填」硬校验
                        subs.forEach(s => { if (!s._new) items.push({ id: s.id, daily_num: +s.num || 0 }); });
                    } else if (!t1._new) {
                        // 无二级 / 二级全被标删 → 一级即末层（后端先删后改，删完一级重新可直填，见 batchUpdateDaily 注释）
                        items.push({ id: t1.id, daily_num: +t1.num || 0 });
                    }
                }));
                return items;
            },
            // 收集本地新增、尚未落库的标签 —— 后端按 level 分两趟落库（一级 → 二级），两趟即完整拓扑序
            // ⭐ 父子都是新增时（先加一级、再在它下面加二级），二级的父此刻**也没有 id**：
            //    用一级的临时标识 _k 作 _tmp_parent_key 指过去，后端建完一级拿到真 id 再回填 pid。
            //    父是已落库一级的，照常给 parent_id。两者互斥（后端对同时给两个是显式拒的）。
            collectAddedLabels() {
                const added = [];
                (this.configGroups || []).forEach(g => (g.tags1 || []).forEach(t1 => {
                    if (t1._del) return;                       // 已标删的一级不可能是新增行（新增行的删除是本地移除）
                    if (t1._new) {
                        added.push({ _tmp_key: t1._k, level: 1, group_id: g.id, name: t1.name, daily_num: +t1.num || 0 });
                    }
                    (t1.subs || []).forEach(s => {
                        if (s._del || !s._new) return;
                        const row = { _tmp_key: s._k, level: 2, name: s.name, daily_num: +s.num || 0 };
                        if (t1._new) row._tmp_parent_key = t1._k; else row.parent_id = t1.id;
                        added.push(row);
                    });
                }));
                return added;
            },
            // 收集被标记删除的**已落库**标签 id（未落库的新增行点 × 直接本地移除，永远不进 deleted_ids —— id>0 守卫兜住）
            // 一级已标删的，其二级不重复提交 —— 后端删一级会级联软删二级（与 delete() 同一处级联语义）
            collectDeletedLabelIds() {
                const ids = [];
                (this.configGroups || []).forEach(g => (g.tags1 || []).forEach(t1 => {
                    if (t1._del) {
                        if (t1.id > 0) ids.push(t1.id);
                        return;
                    }
                    (t1.subs || []).forEach(s => { if (s._del && s.id > 0) ids.push(s.id); });
                }));
                return ids;
            },
            // B3 结转提示：给「已落库、原本没有二级、且已保存过日产能」的一级加了首个二级时，
            // 后端保存时会把该一级已保存的日产能自动转成一条二级「原产能」，一级改为按二级合计（见 Service::carryOverFirstChild）。
            // ⚠️ 结转的是**库里的值** _srvNum，不是用户此刻改了一半的 t1.num（一级一旦有二级就不进 items，改了也不会提交）。
            // 这一条只做**提示**不做本地插行：结转是后端权威逻辑（唯一实现），前端再造一条就会重复插两条「原产能」。
            carryHint(t1) {
                if (t1._new) return 0;                                // 一级本身就是新增 → 库里没有原数可结转
                if (!(+t1._srvNum > 0)) return 0;                     // 已保存的日产能为 0 → 无可结转
                if ((t1.subs || []).some(s => !s._new)) return 0;     // 库里原本就有二级 → 一级早就是 SUM 态
                if (!this.liveSubs(t1).length) return 0;              // 本次还没给它加二级
                return +t1._srvNum;
            },
            // 大类组未识别视频/平面时手选（防御性兜底）
            async setCatType(cat, catType) {
                if (await this._wrap(API.setCapacityCategoryType({ group_id: cat.id, cat_type: catType }), '已设置大类类型')) {
                    this.loadConfigGroups();
                }
            },
            async saveDaily() {
                if (!this.canSaveDaily) return true;
                // 守卫：所有大类必须已定视频/平面，否则排单地基缺失，拦在保存前
                const undecided = (this.cats || []).filter(c => !c.cat_type);
                if (undecided.length) {
                    this.$message.error(`大类「${undecided.map(c => c.name).join('、')}」未设视频/平面，请先在块头选择`);
                    return false;
                }
                const items = this.collectDaily();
                const deletedIds = this.collectDeletedLabelIds();
                const addedItems = this.collectAddedLabels();
                if (!items.length && !deletedIds.length && !addedItems.length) { this.dirtyDaily = false; return true; }
                // 三者同一次提交：后端在同一个事务里 **先删 → 再新增 → 最后改数**
                // （删光二级后一级才允许直填；先删后增才允许「删掉『应用』再加一个『应用』」）
                const ok = await this._wrap(
                    API.batchUpdateCapacityDaily({ items, deleted_ids: deletedIds, added_items: addedItems }), '日产能已保存');
                if (ok) { this.dirtyDaily = false; await this.loadConfigGroups(); }
                return ok;
            },
            // ---- 添加 / 移除部门 ----
            async openAddDialog() {
                this.addSelected = [];
                if (!this.groupTree.length) {
                    try {
                        const { data } = await API.getSchedulingGroups();
                        this.groupTree = data || [];
                    } catch (e) { /* ignore */ }
                }
                this.addDialog = true;
            },
            async confirmAdd() {
                const ids = Array.isArray(this.addSelected) ? this.addSelected : [this.addSelected];
                if (!ids.length) { this.$message.warning('请选择制作组'); return; }
                if (!await this._confirmDiscardIfDirty()) return;
                if (await this._wrap(API.addCapacityConfigGroup({ group_ids: ids }), '已添加部门')) {
                    this.addDialog = false;
                    this.loadConfigGroups();
                }
            },
            removeGroup(g) {
                this.$confirm(`移除部门「${g.name}」？其产能标签将全部清除（移出产能配置）。`, '提示', { type: 'warning' }).then(async () => {
                    if (!await this._confirmDiscardIfDirty()) return;
                    try {
                        const res = await API.removeCapacityConfigGroup({ group_id: g.id });
                        if (res && res.data && res.data.need_migrate) {
                            // 该组有在册排期 → 必须迁移到目标组再删
                            this.openMigrateDialog(g, res.data.pending);
                            return;
                        }
                        this.$message.success('已移除');
                        this.loadConfigGroups();
                    } catch (error) {
                        this.$message.error((error && error.info) || '操作失败');
                    }
                }).catch(() => {});
            },
            openMigrateDialog(g, pending) {
                this.migrate = { visible: true, srcGroup: g, pending: pending || 0, targetGroup: '', loading: false };
            },
            // 删组迁移目标组候选：已纳入制作组（排除被删源组·可跨大类）
            migrateTargetOptions() {
                const srcId = this.migrate.srcGroup ? this.migrate.srcGroup.id : null;
                return (this.configGroups || []).filter(x => x.id !== srcId);
            },
            async confirmMigrate() {
                if (!this.migrate.targetGroup) {
                    this.$message.warning('请选择迁移目标组');
                    return;
                }
                this.migrate.loading = true;
                try {
                    const res = await API.removeCapacityConfigGroup({ group_id: this.migrate.srcGroup.id, target_group_id: this.migrate.targetGroup });
                    this.$message.success((res && res.info) || '已迁移并移除原组');
                    this.migrate.visible = false;
                    this.loadConfigGroups();
                } catch (error) {
                    this.$message.error((error && error.info) || '迁移失败');
                } finally {
                    this.migrate.loading = false;
                }
            },
            // ---- 结构性操作（添加/移除部门）前的未保存修改守卫 ----
            // 这两个动作仍是**即时接口**，且成功后必然 loadConfigGroups() 重载 → 会冲掉本地未保存的日产能修改
            // （改数 / 待删标记 / 待新增标签）。
            // ⚠️ 上一版这里是 _flushIfDirty()：先替用户把改动**落库**再继续。它诞生于「添加一/二级标签会 reload」
            //    的年代（那两个调用点在本轮改造后已消失——加标签现在是纯本地操作，不再 reload）。
            //    剩下的两个调用点继续用它，反而正好重现需求方投诉的原始问题：用户点的是「添加/移除部门」，
            //    **并没有表达「确认提交我标的删除/新增」**，却被静默落库。
            //    故整段改成**放弃语义**：讲清会丢什么，用户点头才继续，绝不代替用户提交。
            //    想保留改动就点取消，先去点「保存日产能」，再回来做部门操作。
            async _confirmDiscardIfDirty() {
                if (!this.dirtyDaily) return true;
                const add = this.addDailyCount;
                const del = this.delDailyCount;
                const parts = [];
                if (add) parts.push(`<b>${add} 项待新增标签</b>`);
                if (del) parts.push(`<b>${del} 项待删除标签</b>`);
                parts.push('已改动的日产能数字');
                return await this.$confirm(
                    `该操作会重新加载产能配置，<b>本次未保存的修改将全部丢失</b>（${parts.join(' / ')}）。<br>`
                    + `想保留请点「取消」，先点「保存日产能」提交后再回来操作。`,
                    '提示',
                    { type: 'warning', dangerouslyUseHTMLString: true, confirmButtonText: '放弃修改并继续', cancelButtonText: '取消' }
                ).then(() => true).catch(() => false);
            },
            // ---- 标签增删：全部本地暂存，点「保存日产能」才一次性提交（关页面 = 放弃）----
            // 与渠道占比 tab 的 addChanRow/addTier 同一心智：＋只动本地树，不打任何接口。
            _labelAddDenied() {
                if (this.canSaveDaily) return false;
                // 新增已并入「保存日产能」链路（capacity_label/batch_update_daily → save_capacity_daily 节点），
                // 只有「标签维护」没有「保存产能」的角色加了也提交不了，直说而不是让人白加一通
                this.$message.warning('新增标签需点「保存日产能」才生效，当前角色无「保存产能」权限，请联系管理员');
                return true;
            },
            // 名称本地预校验：与后端 checkLabelName + nameExists 同口径，先拦一道省一次往返（后端仍硬校验）
            _labelNameInvalid(name, siblings) {
                if (!name) { this.$message.error('名称不能为空'); return true; }
                if (name.length > 60) { this.$message.error('标签名称最长 60 个字符'); return true; }
                if ((siblings || []).some(x => !x._del && x.name === name)) {
                    this.$message.error(`同层级已有同名标签「${name}」`);
                    return true;
                }
                return false;
            },
            addT1(g) {
                if (this._labelAddDenied()) return;
                this.$prompt('输入一级标签名（默认 应用/IAA/IAP，可自定义）', '添加一级标签', { inputPattern: /\S/, inputErrorMessage: '名称不能为空' })
                    .then(({ value }) => {
                        const name = String(value || '').trim();
                        if (!g.tags1) this.$set(g, 'tags1', []);
                        if (this._labelNameInvalid(name, g.tags1)) return;
                        this.labelSeq += 1;
                        // _k 同时是渲染 key 和提交时的 _tmp_key（后端靠它把同批新增的二级挂到这条一级下）
                        g.tags1.push({ _k: 'n' + this.labelSeq, _new: true, _del: false, id: 0, name, num: 0, _srvNum: 0, is_default: 0, subs: [] });
                        this.dirtyDaily = true;
                        this.$message.success('已加到页面上（未保存），点「保存日产能」后才真正创建');
                    }).catch(() => {});
            },
            addSub(t1) {
                if (this._labelAddDenied()) return;
                this.$prompt('输入二级标签名（如 原创/二创/AI/真人）', `在「${t1.name}」下加二级`, { inputPattern: /\S/, inputErrorMessage: '名称不能为空' })
                    .then(({ value }) => {
                        const name = String(value || '').trim();
                        if (!t1.subs) this.$set(t1, 'subs', []);
                        if (this._labelNameInvalid(name, t1.subs)) return;
                        this.labelSeq += 1;
                        t1.subs.push({ _k: 'n' + this.labelSeq, _new: true, _del: false, id: 0, name, num: 0 });
                        this.dirtyDaily = true;
                        const carry = this.carryHint(t1);
                        if (carry) {
                            // 一级原有日产能会在保存时被后端结转成二级「原产能」——此刻页面上一级已变成 Σ二级（暂时显示为小），
                            // 不讲清楚用户会以为自己的数被吃了
                            this.$message({
                                type: 'warning', duration: 8000, dangerouslyUseHTMLString: true,
                                message: `已加到页面上（未保存）。<br>「${t1.name}」已保存的日产能 <b>${carry}</b> 会在保存时自动转成二级<b>「原产能」</b>保留，不会丢。`,
                            });
                        } else {
                            this.$message.success('已加到页面上（未保存），点「保存日产能」后才真正创建');
                        }
                    }).catch(() => {});
            },
            // 删除：**未落库的新增行直接本地移除**（对齐渠道占比 delTier/delChanNode 口径，不进 deleted_ids）；
            //       已落库的只打本地标记，点「保存日产能」才真正提交（关页面 = 放弃删除）
            delLabel(node, isT1, g, t1) {
                if (node._new) {
                    if (isT1) g.tags1 = (g.tags1 || []).filter(x => x !== node);        // 连同其下未保存的二级一起没了
                    else t1.subs = (t1.subs || []).filter(x => x !== node);
                    this.dirtyDaily = true;
                    return;
                }
                if (!this.canSaveDaily) {
                    // 删除已并入「保存日产能」链路（capacity_label/batch_update_daily → save_capacity_daily 节点），
                    // 只有「标签维护」没有「保存产能」的角色标了也提交不了，直说而不是让人白标一通
                    this.$message.warning('删除需点「保存日产能」才生效，当前角色无「保存产能」权限，请联系管理员');
                    return;
                }
                const tip = isT1
                    ? `标记删除一级标签「${node.name}」？连同其二级一并移除。<br><b>删除将在点击「保存日产能」后生效</b>，此前可撤销；直接关页面即放弃。`
                    : `标记删除二级标签「${node.name}」？<br><b>删除将在点击「保存日产能」后生效</b>，此前可撤销；直接关页面即放弃。`;
                this.$confirm(tip, '提示', { type: 'warning', dangerouslyUseHTMLString: true }).then(() => {
                    node._del = true;
                    this.dirtyDaily = true;
                }).catch(() => {});
            },
            undoDelLabel(node) { node._del = false; this.dirtyDaily = true; },
            // ---- 扩充 ----
            async loadExtra() {
                try {
                    const { data } = await API.getCapacityExpandList({});
                    this.extraList = data || [];
                } catch (e) { /* ignore */ }
            },
            async addExtra() {
                if (!this.extraForm.date) { this.$message.warning('请选择日期'); return; }
                if (!this.extraForm.group_id) { this.$message.warning('请选择制作组'); return; }
                const ok = await this._wrap(API.saveCapacityExpand({
                    group_id: this.extraForm.group_id, expand_date: this.extraForm.date,
                    expand_num: this.extraForm.num, remark: '',
                }), '已添加扩充');
                if (ok) { this.extraForm.date = ''; this.extraForm.group_id = ''; this.extraForm.num = 10; this.loadExtra(); }
            },
            delExtra(row) {
                this.$confirm(`删除 ${row.expand_date} ${row.group_name} +${row.expand_num} 的扩充？`, '提示', { type: 'warning' }).then(async () => {
                    if (await this._wrap(API.deleteCapacityExpand({ id: row.id }), '已删除')) this.loadExtra();
                }).catch(() => {});
            },
            // ---- 渠道占比 ----
            async loadPlatOptions() {
                try {
                    const { data } = await API.getSizeConfigPlatList();
                    this.platOptions = (data || []).map(i => ({ id: i.plat_id || i.id, name: i.plat_name || i.name }));
                } catch (e) { /* ignore */ }
            },
            loadAllChannels() {
                this.channelMap = {};
                this.cats.forEach(cat => this.loadChannel(cat.id));
            },
            async loadChannel(groupId) {
                try {
                    const { data } = await API.getChannelRatioList({ group_id: groupId });
                    this.$set(this.channelMap, groupId, this.buildChanTree(data || []));
                } catch (e) { this.$set(this.channelMap, groupId, []); }
            },
            // 扁平五列行 → 以渠道为根的树：app_type=0 是渠道级根行（= 改造前那一行），其余是档级叶行
            // 后端已按「渠道之间 ratio 降序 → 渠道内部根行在前、叶行三维升序」排好，这里按出现顺序建树即可
            buildChanTree(rows) {
                const order = [];
                const map = {};
                (rows || []).forEach(r => {
                    const pid = r.plat_id;
                    if (!map[pid]) {
                        map[pid] = {
                            _k: 'p' + pid, id: 0, plat_id: pid, plat_name: r.plat_name || '',
                            ratio: 0, edit_name: '', mtime: '', expanded: true, _del: false, tiers: [],
                        };
                        order.push(map[pid]);
                    }
                    const node = map[pid];
                    if (!node.plat_name && r.plat_name) node.plat_name = r.plat_name;
                    if (+r.app_type === 0) {
                        // 渠道级根行（带 edit_name/mtime：lastChan() 靠它们挑「最近修改」）
                        node.id = r.id;
                        node.ratio = +r.ratio || 0;
                        node.edit_name = r.edit_name;
                        node.mtime = r.mtime;
                    } else {
                        node.tiers.push({
                            _k: 't' + r.id, id: r.id, _del: false,
                            app_type: +r.app_type, package_type: +r.package_type, game_classify: +r.game_classify || 0,
                            ratio: +r.ratio || 0, edit_name: r.edit_name, mtime: r.mtime,
                        });
                    }
                });
                return order;
            },
            // 树 → 扁平五列行（save 用）：根行三维恒 0；应用叶行 game_classify 恒 0（后端硬校验，不能瞎传）
            // 已标记删除的渠道/组合不进 items（它们进 deleted_ids；同一条既保存又删除后端会显式拒）
            flatChanTree(nodes) {
                const items = [];
                this.liveChanNodes(nodes).forEach(n => {
                    items.push({ plat_id: n.plat_id, app_type: 0, package_type: 0, game_classify: 0, ratio: +n.ratio || 0 });
                    this.liveTiers(n).forEach(t => items.push({
                        plat_id: n.plat_id,
                        app_type: +t.app_type,
                        package_type: +t.package_type,
                        game_classify: +t.app_type === 1 ? 0 : (+t.game_classify || 0),
                        ratio: +t.ratio || 0,
                    }));
                });
                return items;
            },
            // 未被标记删除的渠道 / 档级（合计、校验、提交一律只看这些行——必须与后端合并态口径一致）
            liveChanNodes(nodes) { return (nodes || []).filter(n => !n._del); },
            liveTiers(node) { return (((node && node.tiers) || []).filter(t => !t._del)); },
            chanRound(n) { return Math.round((+n || 0) * 100) / 100; },
            // 渠道级合计（所有未删树根）——页面底部提示
            sumRatio(catId) { return this.chanRound(this.liveChanNodes(this.channelMap[catId]).reduce((a, n) => a + (+n.ratio || 0), 0)); },
            // 档级合计（某棵树内部所有未删叶子）——挂该树根那一行右侧
            tierSum(node) { return this.chanRound(this.liveTiers(node).reduce((a, t) => a + (+t.ratio || 0), 0)); },
            // 三维组合键（与后端五列 upsert 的后三列口径一致）
            tierKey(t) { return [+t.app_type, +t.package_type, +t.app_type === 1 ? 0 : (+t.game_classify || 0)].join('_'); },
            // 本大类待删项数（提示用）
            delChanCount(catId) { return this.collectDeletedChanIds(catId).length; },
            // 收集被标记删除的**已落库**行 id（未落库的新增行直接本地移除，不进 deleted_ids）
            collectDeletedChanIds(catId) {
                const ids = [];
                (this.channelMap[catId] || []).forEach(n => {
                    if (n._del) {
                        // 整棵渠道删：只提根行 id，其档级明细由后端级联软删（与 delete() 同一处级联语义）
                        if (n.id) ids.push(n.id);
                        return;
                    }
                    // 「删掉旧组合 + 又新加了同维度组合」时旧行会被 upsert 复用复活，
                    // 此时不能再把它提进 deleted_ids —— 后端对「同一条既保存又删除」是显式拒的
                    const liveKeys = {};
                    this.liveTiers(n).forEach(t => { liveKeys[this.tierKey(t)] = 1; });
                    (n.tiers || []).forEach(t => {
                        if (t._del && t.id && !liveKeys[this.tierKey(t)]) ids.push(t.id);
                    });
                });
                return ids;
            },
            // 绝对占比 = 渠道级 × 档级 ÷ 100
            absRatio(node, t) { return this.chanRound((+node.ratio || 0) * (+t.ratio || 0) / 100); },
            tierText(t) {
                const a = (this.appTypeOptions.find(o => o.v == t.app_type) || {}).l || '-';
                const p = (this.packageTypeOptions.find(o => o.v == t.package_type) || {}).l || '-';
                if (+t.app_type === 2) {
                    const g = (this.gameClassifyOptions.find(o => o.v == t.game_classify) || {}).l || '-';
                    return `${a} - ${p} - ${g}`;
                }
                return `${a} - ${p}`;
            },
            // 该大类组渠道占比最近修改（未删的根行 + 叶行里取 mtime 最大那行）
            lastChan(catId) {
                const rows = [];
                this.liveChanNodes(this.channelMap[catId]).forEach(n => {
                    if (n.id && n.mtime) rows.push(n);
                    this.liveTiers(n).forEach(t => { if (t.id && t.mtime) rows.push(t); });
                });
                if (!rows.length) return null;
                return rows.reduce((a, b) => (a.mtime > b.mtime ? a : b));
            },
            // ＋添加渠道 = 加一棵树根
            addChanRow(catId) {
                const platId = this.newPlat[catId];
                if (!platId) { this.$message.warning('请选择渠道'); return; }
                const nodes = this.channelMap[catId] || [];
                const exist = nodes.find(n => n.plat_id == platId);
                if (exist) {
                    // 标了删又重新加同一个渠道 = 用户改主意了 → 直接撤销删除标记，
                    // 不能既加又删（后端对「同一条既保存又删除」显式拒），也不该让人看着「已在列表」一头雾水
                    if (exist._del) {
                        exist._del = false;
                        this.$set(this.newPlat, catId, '');
                        this.$message.success('该渠道原已标记删除，已撤销删除标记');
                        return;
                    }
                    this.$message.warning('该渠道已在列表');
                    return;
                }
                const plat = this.platOptions.find(p => p.id == platId);
                nodes.push({
                    _k: 'p' + platId, id: 0, plat_id: platId, plat_name: plat ? plat.name : '',
                    ratio: 0, edit_name: '', mtime: '', expanded: true, _del: false, tiers: [],
                });
                this.$set(this.channelMap, catId, nodes);
                this.$set(this.newPlat, catId, '');
            },
            // ＋添加组合 = 在该渠道下加一条 (产品类型, 包体类型, 游戏类型) 分支
            addTier(node) {
                this.tierSeq += 1;
                node.tiers.push({ _k: 'n' + this.tierSeq, id: 0, _del: false, app_type: '', package_type: '', game_classify: '', ratio: 0 });
                node.expanded = true;
            },
            // 应用没有游戏类型这一层：切到「应用」时清空，落库 game_classify=0
            onTierAppType(t) { if (+t.app_type === 1) t.game_classify = ''; },
            // 删档级组合：未落库的新增行直接本地移除；已落库的只打标记，点「保存占比」才提交
            delTier(catId, node, t) {
                if (!t.id) { node.tiers = node.tiers.filter(x => x !== t); return; }
                const tip = `标记删除组合「${this.tierText(t)}」？<br><b>删除将在点击「保存占比」后生效</b>，此前可撤销；直接关页面即放弃。`
                    + `<br>保存时该渠道剩余档级仍需合计 100%。`;
                this.$confirm(tip, '提示', { type: 'warning', dangerouslyUseHTMLString: true })
                    .then(() => { t._del = true; }).catch(() => {});
            },
            // 删整棵渠道树：后端删根行会级联软删该渠道全部叶行，必须先跟用户讲清楚
            delChanNode(catId, node) {
                if (!node.id) { this.$set(this.channelMap, catId, (this.channelMap[catId] || []).filter(n => n !== node)); return; }
                const cnt = this.liveTiers(node).length;
                const tip = (cnt
                    ? `标记删除渠道「${node.plat_name}」？将连同其下全部 ${cnt} 条档级明细一并级联删除。`
                    : `标记删除渠道「${node.plat_name}」？仅影响该大类看板占比。`)
                    + `<br><b>删除将在点击「保存占比」后生效</b>，此前可撤销；直接关页面即放弃。`
                    + `<br>保存时剩余渠道级占比仍需合计 100%（全部删光则视为该大类不配占比，可直接保存）。`;
                this.$confirm(tip, '提示', { type: 'warning', dangerouslyUseHTMLString: true })
                    .then(() => { node._del = true; }).catch(() => {});
            },
            undoDelChan(node) { node._del = false; },
            undoDelTier(t) { t._del = false; },
            // 前端预校验：与后端硬校验同口径（维度必填 / 同渠道组合唯一 / 两层合计各 100%）
            // ⚠️ 合计必须**排除已标记删除的行**再算，否则前端拦到的数和后端合并态对不上：
            //    用户删掉一个 30% 的渠道，前端若还把它计进去就永远显示 100% 放行，后端却按 70% 拒 —— 两头打架
            validateChannel(catId) {
                const all = this.channelMap[catId] || [];
                if (!all.length) return '请先添加渠道';
                const nodes = this.liveChanNodes(all);
                // 全部标记删除 = 清空该大类占比（与「从没配过」等价），后端同口径放行，不校验 100%
                if (!nodes.length) return '';
                const errs = [];
                nodes.forEach(n => {
                    const seen = {};
                    this.liveTiers(n).forEach(t => {
                        if (!+t.app_type) { errs.push(`【${n.plat_name}】请选择产品类型`); return; }
                        if (!+t.package_type) { errs.push(`【${n.plat_name}】请选择包体类型`); return; }
                        if (+t.app_type === 2 && !+t.game_classify) {
                            errs.push(`【${n.plat_name}】产品类型为「游戏」时必须选择游戏类型（IAA/IAP）`);
                            return;
                        }
                        const key = this.tierKey(t);
                        if (seen[key]) { errs.push(`同一渠道下该组合重复：${n.plat_name} ${this.tierText(t)}`); return; }
                        seen[key] = 1;
                    });
                    // 只加渠道不打标 = 退化为改造前口径，合法，不校验档级
                    if (this.liveTiers(n).length) {
                        const ts = this.tierSum(n);
                        if (Math.abs(ts - 100) > 0.001) errs.push(`【${n.plat_name}】档级占比合计 ${ts}%，需配满 100%`);
                    }
                });
                const s = this.sumRatio(catId);
                if (Math.abs(s - 100) > 0.001) errs.push(`渠道级占比合计 ${s}%，需配满 100%`);
                return errs.filter((v, i) => errs.indexOf(v) === i).join('；');
            },
            async saveChannel(catId) {
                const err = this.validateChannel(catId);
                if (err) { this.$message.error(err); return; }
                const items = this.flatChanTree(this.channelMap[catId]);
                const deletedIds = this.collectDeletedChanIds(catId);
                if (!items.length && !deletedIds.length) { this.$message.warning('无修改项'); return; }
                // deleted_ids 与 items 同一次提交：后端在同一个事务里软删，且合计合并态会先剔除这些行
                if (await this._wrap(API.saveChannelRatio({ group_id: catId, items, deleted_ids: deletedIds }), '已保存')) this.loadChannel(catId);
            },
            // ---- 历史修改记录 ----
            async loadHistory() {
                if (this.historyLoading) return;   // 防连点重复触发
                this.historyLoading = true;
                try {
                    const { data } = await API.getCapacityHistory({
                        page: this.historyPage, page_size: this.historyPageSize, type: this.historyType || '',
                    });
                    this.historyList = (data && data.list) || [];
                    this.historyTotal = (data && data.total) || 0;
                } catch (error) {
                    this.$message.error(error.info || '加载失败');
                } finally {
                    this.historyLoading = false;
                }
            },
            reloadHistory() { this.historyPage = 1; this.loadHistory(); },
            onHistoryPage(p) { this.historyPage = p; this.loadHistory(); },
            // ---- 公共 ----
            async _wrap(promise, okMsg, silent) {
                try {
                    await promise;
                    if (!silent) this.$message.success(okMsg || '操作成功');
                    return true;
                } catch (error) {
                    this.$message.error((error && error.info) || '操作失败');
                    return false;
                }
            },
        },
    };
</script>

<style scoped>
    .cap-dash { --blue:#409EFF; --green:#67C23A; --red:#F56C6C; --purple:#8e7cc3; --bd:#EBEEF5; --text:#303133; --sub:#909399; }
    .cap-dash .tg-edit { color:#909399; font-size:12px; margin-left:12px; }
    .cap-dash .hist-bar { margin-bottom:10px; }
    .cap-dash .mr10 { margin-right:10px; }
    .cap-dash .tar { text-align:right; }
    .cap-dash .mt10 { margin-top:10px; }
    .mr6 { margin-right:6px; } .ml4 { margin-left:4px; } .ml10 { margin-left:10px; }
    .lbl-tip { font-size:11px; color:#a8abb2; }
    .ml6 { margin-left:6px; }
    .cat-undecided { color:#F56C6C; font-size:12px; margin-left:6px; }
    .cat-undecided .el-button { margin-left:4px; }

    /* 已标记删除（尚未保存）：划掉 + 变灰，撤销按钮本身不参与置灰，否则用户看不清怎么反悔 */
    .row-del { opacity:.55; background:#fafafa; }
    .row-del .t1-name, .row-del .cr-name, .row-del .tier-text, .row-del .sub { text-decoration:line-through; }
    .row-del.sub { text-decoration:line-through; }
    .row-del .undo-btn { opacity:1; text-decoration:none; }
    .del-flag { font-size:12px; color:#E6A23C; }

    /* 本地新增（尚未保存）：左侧绿条 + 「未保存」提示，与「已标记删除」的灰划掉区分开 */
    .row-new { background:#f6fff0; border-left:3px solid #67C23A; }
    .new-flag { font-size:12px; color:#67C23A; }
    .new-dot { font-style:normal; font-size:10px; line-height:14px; padding:0 4px; margin-left:4px; border-radius:3px; background:#67C23A; color:#fff; }
    .carry-hint { font-size:12px; color:#E6A23C; }
    .carry-hint b { color:#E6A23C; }
    .undo-btn { height:24px; padding:0 8px; border:1px solid #E6A23C; border-radius:4px; background:#fdf6ec; color:#E6A23C; cursor:pointer; font-size:12px; }
    .undo-btn:hover { background:#E6A23C; color:#fff; }
    .undo-btn.mini { height:20px; padding:0 5px; font-size:11px; }

    .cap-subtabs { display:flex; gap:4px; border-bottom:2px solid var(--bd); margin-bottom:16px; }
    .cap-subtab { background:none; border:none; padding:8px 18px; font-size:13px; font-weight:600; cursor:pointer; color:#606266; border-bottom:2px solid transparent; margin-bottom:-2px; }
    .cap-subtab:hover { color:var(--blue); }
    .cap-subtab.active { color:var(--blue); border-bottom-color:var(--blue); }

    .dayrate-banner { background:#ecf5ff; border:1px solid #b3d8ff; border-left:4px solid var(--blue); padding:8px 12px; border-radius:4px; font-size:12px; line-height:1.7; margin-bottom:8px; }
    .dayrate-banner b { color:var(--blue); }
    .confirm-note { font-size:12px; font-weight:500; color:#c0392b; line-height:1.8; background:#fff7f7; border:1px solid #fde2e2; border-radius:4px; padding:6px 10px; margin-bottom:8px; }
    .confirm-note b { color:#c0392b; }
    .add-dept-bar { display:flex; align-items:center; gap:10px; margin-bottom:12px; }

    .cap-tree-cat { margin-bottom:20px; background:#fff; border:1px solid var(--bd); border-radius:8px; overflow:hidden; }
    .tree-cat-head { background:#ecf5ff; padding:12px 16px; display:flex; justify-content:space-between; align-items:center; }
    .tree-cat-head .tc-name { font-weight:700; font-size:15px; }
    .tree-cat-head .tc-total { font-size:12px; color:#606266; }
    .tree-cat-head .tc-total b { font-size:16px; color:var(--blue); }
    .tree-group { border-top:1px solid var(--bd); }
    .tree-group-head { padding:10px 16px; background:#fafafa; display:flex; justify-content:space-between; align-items:center; }
    .tree-group-head .tg-name { font-weight:600; }
    .tree-group-head .tg-right { display:flex; align-items:center; gap:14px; }
    .grp-pill { display:inline-block; padding:1px 8px; border-radius:4px; background:#f0eefb; color:var(--purple); font-size:12px; margin-left:6px; }
    .tree-group-head .tg-sum { font-size:12px; color:var(--sub); }
    .tree-group-head .tg-sum b { color:var(--text); }
    .rm-grp { color:var(--red); padding:0; }
    .tree-t1 { padding:8px 16px 8px 32px; border-top:1px dashed var(--bd); }
    .tree-t1-head { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
    .tree-t1-head .t1-name { font-weight:600; color:#606266; font-size:12px; min-width:48px; }
    .tree-t1-head .t1-num { font-size:12px; color:var(--sub); display:flex; align-items:center; gap:6px; }
    .tree-t1-head .t1-num input { width:56px; height:28px; border:1px solid #dcdfe6; border-radius:4px; padding:0 8px; font-size:12px; outline:none; }
    .tree-t1-head .t1-num input:focus { border-color:var(--blue); }
    .tree-t1-head .t1-num b { color:var(--text); }
    .tree-t1-head .t1-num.readonly { background:#f7f9fc; border:1px dashed var(--bd); border-radius:4px; padding:2px 8px; }
    .tree-t1-head .t1-actions { display:flex; align-items:center; gap:8px; margin-left:auto; }
    .sub-add { height:26px; padding:0 10px; border:1px dashed var(--blue); border-radius:4px; background:#ecf5ff; color:var(--blue); cursor:pointer; font-size:12px; }
    .sub-add:hover { background:var(--blue); color:#fff; }
    .t1-del { cursor:pointer; font-style:normal; color:#c0c4cc; font-size:16px; line-height:1; padding:0 4px; border-radius:3px; }
    .t1-del:hover { color:#fff; background:var(--red); }
    .tree-subs { display:flex; flex-wrap:wrap; gap:14px; padding:8px 0 2px 58px; }
    .tree-subs-empty { font-size:11px; color:#a8abb2; padding:4px 0 2px 58px; }
    .sub { display:flex; align-items:center; gap:6px; font-size:12px; color:#606266; }
    .sub input { width:52px; height:26px; border:1px solid #dcdfe6; border-radius:4px; padding:0 6px; font-size:12px; outline:none; }
    .sub input:focus { border-color:var(--blue); }
    .sub-del { cursor:pointer; font-style:normal; color:#c0c4cc; font-size:14px; line-height:1; padding:0 2px; border-radius:3px; }
    .sub-del:hover { color:#fff; background:var(--red); }
    .t1-add-row { padding:8px 16px 10px 32px; border-top:1px dashed var(--bd); }
    .t1-add { height:30px; padding:0 14px; border:1px dashed var(--blue); border-radius:4px; background:#ecf5ff; color:var(--blue); cursor:pointer; font-size:13px; }
    .t1-add:hover { background:var(--blue); color:#fff; }
    .t1-empty { padding:10px 32px; color:#a8abb2; font-size:12px; }

    .extra-form { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:14px; }
    .extra-form .lbl-tip { margin-left:4px; }

    .chan-tip { font-size:12px; color:#909399; margin-bottom:12px; line-height:1.9; }
    .chan-tip b { color:#606266; }
    .chan-cat { background:#fff; border:1px solid var(--bd); border-radius:8px; padding:14px 16px; margin-bottom:16px; }
    .chan-cat-head { display:flex; justify-content:space-between; align-items:center; margin-bottom:12px; }
    .chan-cat-head .cc-name { font-weight:700; font-size:14px; }
    .chan-cat-head .cc-total { font-size:12px; color:var(--sub); }
    .chan-rows { display:flex; flex-direction:column; gap:8px; }
    .chan-tree { border:1px solid var(--bd); border-radius:6px; padding:8px 10px; background:#fcfcfd; }
    .chan-row { display:flex; align-items:center; gap:12px; }
    .chan-row .cr-fold { cursor:pointer; color:var(--sub); font-size:14px; width:14px; }
    .chan-row .cr-fold:hover { color:var(--blue); }
    .chan-row .cr-name { width:80px; color:#606266; font-weight:600; }
    .chan-row .cr-tsum { width:230px; font-size:12px; text-align:right; }
    .chan-row .cr-tsum.ok { color:var(--green); }
    .chan-row .cr-tsum.bad { color:var(--red); }
    .chan-row .cr-tsum.none { color:#a8abb2; }
    .chan-row .cr-tsum.del { color:#E6A23C; width:300px; text-decoration:none; }
    .chan-row .cr-bar { flex:1; height:14px; background:#f0f2f5; border-radius:7px; overflow:hidden; }
    .chan-row .cr-bar > i { display:block; height:100%; background:var(--purple); border-radius:7px; transition:width .3s; }
    .chan-row .cr-pct input { width:56px; height:26px; border:1px solid #dcdfe6; border-radius:4px; padding:0 6px; text-align:right; }
    .chan-row .cr-num { width:96px; text-align:right; font-size:12px; color:var(--sub); }
    .chan-row .cr-del { width:22px; height:22px; line-height:20px; text-align:center; padding:0; border:1px solid #dcdfe6; border-radius:4px; background:#fff; color:#c0c4cc; cursor:pointer; }
    .chan-row .cr-del:hover { border-color:var(--red); color:var(--red); }
    .chan-tiers { padding:6px 0 0 26px; }
    .chan-tier { display:flex; align-items:center; gap:10px; padding:4px 0; }
    .chan-tier .tier-text { width:220px; font-size:12px; color:#606266; }
    .chan-tier .tier-na { width:96px; font-size:11px; color:#a8abb2; }
    .chan-tier .cr-bar { flex:1; height:10px; background:#f0f2f5; border-radius:5px; overflow:hidden; }
    .chan-tier .cr-bar > i { display:block; height:100%; background:#b6a9dd; border-radius:5px; transition:width .3s; }
    .chan-tier .cr-pct input { width:56px; height:26px; border:1px solid #dcdfe6; border-radius:4px; padding:0 6px; text-align:right; }
    .chan-tier .cr-num { width:96px; text-align:right; font-size:12px; color:var(--sub); }
    .chan-tier .cr-del { width:22px; height:22px; line-height:20px; text-align:center; padding:0; border:1px solid #dcdfe6; border-radius:4px; background:#fff; color:#c0c4cc; cursor:pointer; }
    .chan-tier .cr-del:hover { border-color:var(--red); color:var(--red); }
    .tier-empty { font-size:11px; color:#a8abb2; padding:2px 0; }
    .tier-add-row { padding:6px 0 2px; }
    .tier-add { height:26px; padding:0 10px; border:1px dashed var(--purple); border-radius:4px; background:#f6f4fc; color:var(--purple); cursor:pointer; font-size:12px; }
    .tier-add:hover { background:var(--purple); color:#fff; }
    .chan-empty { font-size:12px; color:#a8abb2; }
    .chan-add-bar { display:flex; align-items:center; gap:8px; margin-top:10px; }
    .cr-add { height:28px; padding:0 12px; border:1px dashed var(--blue); border-radius:4px; background:#ecf5ff; color:var(--blue); cursor:pointer; font-size:12px; }
    .cr-add:hover { background:var(--blue); color:#fff; }
    .chan-sum { margin-top:12px; font-size:12px; }
    .chan-sum.ok { color:var(--green); }
    .chan-sum.bad { color:var(--red); }
    .migrate-warn { font-size:13px; line-height:1.7; color:#5a5e66; background:#fdf6ec; border:1px solid #f5dab1; border-radius:4px; padding:10px 12px; }
    .migrate-warn .hot { color:#f56c6c; font-size:15px; }
    .migrate-cost { margin-top:8px; color:#e6a23c; }
    .migrate-pick { margin-top:14px; display:flex; align-items:center; gap:10px; }
    .migrate-pick .mp-lbl { font-size:13px; color:#333; }
</style>
